import React from 'react';
import { Box, Drawer, IconButton, Stack, SvgIcon, Typography } from '@mui/material';
import JobFullDetails from './JobFullDetails';
import ShareJob from './ShareJob';
import { useBackNavigationDialog } from 'hooks/useBackNavigationDialog'; // Import the hook

const backIcon = `${process.env.REACT_APP_HOMEPAGE}assets/back-arrow-white.png`;

/**
 * JobFullDetailsMobile is a React component that displays the job details page in the mobile sidebar.
 * It takes the following props:
 * - tab: The current tab being viewed.
 * - fetchJobPreferences: Function to fetch job preferences.
 * - application: The current job application object.
 * - onApplied: Callback function to handle actions when a job is applied.
 * - onStatusChange: Callback function to handle changes in application status.
 * - downloadResume: Function to download the resume.
 * - showExternalJobStatus: Boolean to display external job status.
 * - updateTJScore: Function to update the TJ score.
 * - onClose: A function that is called when the component should close.
 *
 * The component displays a header with a back button and the job title, and a content area with the job details.
 * It also includes a drawer at the bottom of the screen that allows users to share the job.
 */
const JobFullDetailsMobile = ({ tab, fetchJobPreferences, application, onApplied, onStatusChange, downloadResume, showExternalJobStatus, updateTJScore, onClose }) => {
  // const [state, setState] = React.useState(false);
  const { openDialog, closeDialog, dialogLocation } = useBackNavigationDialog();
  const dialogIds = dialogLocation.state?.dialogIds || [];
  const isShareDrawerOpen = dialogIds.includes("shareJobDrawer");

  /**
   * Opens the drawer at the bottom of the screen for sharing the job.
   */
  const handleOpenShareDrawer = () => {
    openDialog('shareJobDrawer');
  };

  /**
   * Closes the drawer at the bottom of the screen for sharing the job.
   */
  const handleCloseShareDrawer = () => {
    closeDialog('shareJobDrawer');
  };

  return (
    <Box className="sidebar-wrapper job-details-mob-page">
      <Box className="sidebar-header">
        <Stack direction="row" justifyContent={'space-between'} alignItems={'center'} sx={{ width: '100%' }}>
          <Stack direction="row" justifyContent={'flex-start'} alignItems={'center'} spacing={2}>
            <IconButton aria-label="delete" sx={{ padding: 0 }} onClick={onClose}>
              <img src={backIcon} alt="Back" width="20px" height="20px" loading="lazy" />
            </IconButton>
            <Typography className="title">Job Details</Typography>
          </Stack>
          <IconButton aria-label="share" sx={{ padding: 0 }} onClick={handleOpenShareDrawer} >
            <SvgIcon>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M0.838104 8.37539C0.838473 7.82722 1.01107 7.29302 1.3315 6.84826C1.65194 6.4035 2.10402 6.07067 2.62387 5.89677C3.14373 5.72288 3.70508 5.71672 4.22863 5.87916C4.75218 6.04161 5.21145 6.36444 5.54157 6.80206L7.7015 5.55462L10.1104 4.16386C9.84741 3.54743 9.82954 2.85387 10.0604 2.22471C10.2913 1.59555 10.7536 1.07821 11.3529 0.778256C11.9523 0.478301 12.6435 0.41834 13.2855 0.610608C13.9275 0.802877 14.472 1.23288 14.8078 1.81288C15.1436 2.39288 15.2454 3.07916 15.0925 3.73167C14.9395 4.38418 14.5434 4.95375 13.9849 5.32416C13.4263 5.69456 12.7475 5.83788 12.0869 5.72486C11.4263 5.61185 10.8337 5.25102 10.4301 4.71601L8.02117 6.10606L5.86036 7.35314C6.01361 7.71505 6.0835 8.1068 6.06484 8.49938C6.04618 8.89196 5.93945 9.27531 5.75255 9.62105L10.4301 12.3218C10.5034 12.2246 10.5833 12.1327 10.6693 12.0467C11.1281 11.5889 11.741 11.3184 12.3885 11.2879C13.0359 11.2575 13.6715 11.4692 14.1712 11.8819C14.671 12.2946 14.9992 12.8787 15.0917 13.5202C15.1843 14.1617 15.0346 14.8147 14.6718 15.3518C14.3091 15.889 13.7593 16.2717 13.1297 16.4255C12.5 16.5793 11.8357 16.4931 11.2662 16.1837C10.6966 15.8742 10.2628 15.3638 10.0492 14.7519C9.83557 14.1399 9.85753 13.4704 10.1108 12.8737L5.38014 10.1425C5.35468 10.1704 5.32857 10.1977 5.30182 10.2243C4.9361 10.59 4.4702 10.8389 3.963 10.9398C3.4558 11.0406 2.93009 10.9888 2.45233 10.7909C1.97457 10.593 1.56621 10.2579 1.27889 9.82797C0.991563 9.39801 0.83817 8.89251 0.838104 8.37539Z" fill="white" />
              </svg>
            </SvgIcon>
          </IconButton>
        </Stack>
      </Box>
      <Box className="sidebar-content">
        <JobFullDetails tab={tab} fetchJobPreferences={fetchJobPreferences} application={application} onApplied={onApplied} onStatusChange={onStatusChange} downloadResume={downloadResume} showExternalJobStatus={showExternalJobStatus} updateTJScore={updateTJScore} handleCloseDrawer={onClose} />
      </Box>
      <Drawer
        anchor="bottom"
        open={isShareDrawerOpen}
        onClose={handleCloseShareDrawer}
        className="forgot-password-dialog"
      >
        <ShareJob jobId={application?.job_id?.jobid} onClose={handleCloseShareDrawer} />
      </Drawer>
    </Box>
  );
}

export default JobFullDetailsMobile;